/**
 * Session Storage Manager with LRU Cache
 *
 * Manages sessionStorage with Least Recently Used (LRU) eviction to prevent
 * QuotaExceededError on iOS Safari and other browsers with strict storage limits.
 *
 * Key Features:
 * - Automatic eviction of oldest threads when limit reached
 * - Graceful degradation on quota errors
 * - Cleanup of stale data
 * - Thread access time tracking for smart eviction
 */

import type { Message } from "@/features/chat/types/sdk";
import { STORAGE_CONFIG } from "@/config";
import { conversation_access_times_KEY, THREAD_MESSAGE_PREFIX } from "@/constants";

interface ThreadAccessTimes {
  [threadId: string]: number; // Unix timestamp of last access
}

interface StorageConfig {
  maxCachedThreads: number;
  staleThresholdDays: number;
  enableWarnings: boolean;
}

const DEFAULT_CONFIG: StorageConfig = {
  maxCachedThreads: STORAGE_CONFIG.MAX_CACHED_THREADS,
  staleThresholdDays: STORAGE_CONFIG.STALE_THRESHOLD_DAYS,
  enableWarnings: STORAGE_CONFIG.ENABLE_STORAGE_WARNINGS,
};

/**
 * Session Storage Manager with LRU eviction
 */
class SessionStorageManager {
  private config: StorageConfig;

  // Lock to prevent concurrent cleanup operations from conflicting
  // This prevents race conditions where multiple threads try to evict at the same time
  private isCleanupInProgress = false;

  // Queue for pending operations when cleanup is in progress
  private pendingOperations: Array<() => void> = [];

  constructor(config: Partial<StorageConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Process any operations that were queued during cleanup
   */
  private processPendingOperations(): void {
    const operations = [...this.pendingOperations];
    this.pendingOperations = [];
    operations.forEach((op) => op());
  }

  /**
   * Get thread access times from storage
   */
  private getAccessTimes(): ThreadAccessTimes {
    try {
      const data = sessionStorage.getItem(conversation_access_times_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error("Failed to read thread access times:", error);
      return {};
    }
  }

  /**
   * Update thread access time
   */
  private updateAccessTime(threadId: string): void {
    try {
      const times = this.getAccessTimes();
      times[threadId] = Date.now();
      sessionStorage.setItem(
        conversation_access_times_KEY,
        JSON.stringify(times)
      );
    } catch (error) {
      // Silently fail - access time tracking is non-critical
      console.warn("Failed to update thread access time:", error);
    }
  }

  /**
   * Get all cached thread IDs sorted by access time (oldest first)
   */
  private getCachedThreads(): string[] {
    const times = this.getAccessTimes();
    return Object.entries(times)
      .sort(([, timeA], [, timeB]) => timeA - timeB) // Oldest first
      .map(([threadId]) => threadId);
  }

  /**
   * Evict the least recently used thread
   */
  private evictOldestThread(): boolean {
    const threads = this.getCachedThreads();
    if (threads.length === 0) return false;

    const oldestThreadId = threads[0];
    this.removeThreadMessages(oldestThreadId);

    if (this.config.enableWarnings) {
      console.log(
        `[StorageManager] Evicted oldest thread: ${oldestThreadId}`
      );
    }

    return true;
  }

  /**
   * Attempt to free up storage space (with lock protection)
   */
  private attemptCleanup(): boolean {
    // Prevent concurrent cleanup operations
    if (this.isCleanupInProgress) {
      if (this.config.enableWarnings) {
        console.log("[StorageManager] Cleanup already in progress, skipping");
      }
      return false;
    }

    this.isCleanupInProgress = true;
    let freedSpace = false;

    try {
      // 1. Remove stale threads
      const staleCount = this.removeStaleThreads();
      if (staleCount > 0) {
        freedSpace = true;
        if (this.config.enableWarnings) {
          console.log(`[StorageManager] Removed ${staleCount} stale threads`);
        }
      }

      // 2. Evict oldest thread if we're over the limit
      const threads = this.getCachedThreads();
      if (threads.length >= this.config.maxCachedThreads) {
        freedSpace = this.evictOldestThread() || freedSpace;
      }
    } finally {
      this.isCleanupInProgress = false;
      // Process any operations that were queued during cleanup
      this.processPendingOperations();
    }

    return freedSpace;
  }

  /**
   * Safely set an item in sessionStorage with quota handling
   */
  private safeSetItem(key: string, value: string): boolean {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === "QuotaExceededError") {
        if (this.config.enableWarnings) {
          console.warn(
            "[StorageManager] QuotaExceededError - attempting cleanup"
          );
        }

        // Attempt cleanup and retry once
        const cleanedUp = this.attemptCleanup();
        if (cleanedUp) {
          try {
            sessionStorage.setItem(key, value);
            return true;
          } catch (retryError) {
            console.error(
              "[StorageManager] Storage quota exceeded even after cleanup:",
              retryError
            );
          }
        }
      } else {
        console.error("[StorageManager] Failed to set storage item:", error);
      }
      return false;
    }
  }

  /**
   * Store thread messages with LRU eviction
   * If cleanup is in progress, queues the operation for later execution.
   */
  setThreadMessages(threadId: string, messages: Message[]): boolean {
    // If cleanup is in progress, queue this operation for later
    if (this.isCleanupInProgress) {
      if (this.config.enableWarnings) {
        console.log(`[StorageManager] Queueing setThreadMessages for ${threadId} (cleanup in progress)`);
      }
      this.pendingOperations.push(() => this.setThreadMessages(threadId, messages));
      return true; // Optimistically return true since we'll retry
    }

    const sessionKey = `${THREAD_MESSAGE_PREFIX}${threadId}`;

    // Attempt to store
    const success = this.safeSetItem(sessionKey, JSON.stringify(messages));

    if (success) {
      // Update access time on successful storage
      this.updateAccessTime(threadId);
    }

    return success;
  }

  /**
   * Retrieve thread messages
   */
  getThreadMessages(threadId: string): Message[] | null {
    try {
      const sessionKey = `${THREAD_MESSAGE_PREFIX}${threadId}`;
      const data = sessionStorage.getItem(sessionKey);

      if (data) {
        // Update access time on successful retrieval
        this.updateAccessTime(threadId);
        return JSON.parse(data);
      }

      return null;
    } catch (error) {
      console.error(
        `[StorageManager] Failed to retrieve messages for thread ${threadId}:`,
        error
      );
      return null;
    }
  }

  /**
   * Remove messages for a specific thread
   */
  removeThreadMessages(threadId: string): void {
    try {
      const sessionKey = `${THREAD_MESSAGE_PREFIX}${threadId}`;
      sessionStorage.removeItem(sessionKey);

      // Remove from access times
      const times = this.getAccessTimes();
      delete times[threadId];
      sessionStorage.setItem(
        conversation_access_times_KEY,
        JSON.stringify(times)
      );
    } catch (error) {
      console.error(
        `[StorageManager] Failed to remove thread ${threadId}:`,
        error
      );
    }
  }

  /**
   * Remove threads older than the stale threshold
   */
  removeStaleThreads(): number {
    const staleTimestamp =
      Date.now() - this.config.staleThresholdDays * 24 * 60 * 60 * 1000;
    const times = this.getAccessTimes();
    let removedCount = 0;

    for (const [threadId, accessTime] of Object.entries(times)) {
      if (accessTime < staleTimestamp) {
        this.removeThreadMessages(threadId);
        removedCount++;
      }
    }

    return removedCount;
  }

  /**
   * Clean up orphaned thread entries (thread_messages_* without access time)
   */
  cleanupOrphanedEntries(): number {
    try {
      const times = this.getAccessTimes();
      const knownThreadIds = new Set(Object.keys(times));
      let removedCount = 0;

      // Iterate through all sessionStorage keys
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (
          key &&
          key.startsWith(THREAD_MESSAGE_PREFIX) &&
          key !== THREAD_MESSAGE_PREFIX
        ) {
          const threadId = key.replace(THREAD_MESSAGE_PREFIX, "");
          if (!knownThreadIds.has(threadId)) {
            sessionStorage.removeItem(key);
            removedCount++;
          }
        }
      }

      if (removedCount > 0 && this.config.enableWarnings) {
        console.log(
          `[StorageManager] Cleaned up ${removedCount} orphaned entries`
        );
      }

      return removedCount;
    } catch (error) {
      console.error("[StorageManager] Failed to cleanup orphaned entries:", error);
      return 0;
    }
  }

  /**
   * Perform full cleanup (stale threads + orphaned entries)
   */
  performFullCleanup(): { staleThreads: number; orphanedEntries: number } {
    const staleThreads = this.removeStaleThreads();
    const orphanedEntries = this.cleanupOrphanedEntries();

    if (this.config.enableWarnings) {
      console.log(
        `[StorageManager] Full cleanup complete: ${staleThreads} stale threads, ${orphanedEntries} orphaned entries`
      );
    }

    return { staleThreads, orphanedEntries };
  }

  /**
   * Clear all cached thread messages and access times
   * Used when URL parameters change to ensure clean state
   */
  clearAll(): number {
    try {
      const times = this.getAccessTimes();
      const threadIds = Object.keys(times);
      let removedCount = 0;

      // Remove all thread message entries
      for (const threadId of threadIds) {
        const sessionKey = `${THREAD_MESSAGE_PREFIX}${threadId}`;
        sessionStorage.removeItem(sessionKey);
        removedCount++;
      }

      // Clear access times metadata
      sessionStorage.removeItem(conversation_access_times_KEY);

      if (this.config.enableWarnings) {
        console.log(
          `[StorageManager] Cleared all cached threads: ${removedCount} threads removed`
        );
      }

      return removedCount;
    } catch (error) {
      console.error("[StorageManager] Failed to clear all cached threads:", error);
      return 0;
    }
  }
}

// Export singleton instance with default config
export const storageManager = new SessionStorageManager();
